import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const accesoRestringidoStyles = {
  container: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' },
  card: { backgroundColor: 'white', padding: '2rem 3rem', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', textAlign: 'center', maxWidth: '500px' },
  icon: { fontSize: '4rem', display: 'block', marginBottom: '1rem' },
  message: { color: '#6b7280', marginBottom: '1.5rem', lineHeight: '1.6' },
  suggestion: { fontSize: '0.9rem', color: '#9ca3af', marginBottom: '2rem' },
  button: { backgroundColor: '#3b82f6', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', fontSize: '1rem', cursor: 'pointer' },
};

const Servicios = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [accesoDenegado, setAccesoDenegado] = useState(false);
  const [mensajeError, setMensajeError] = useState('');
  const [servicios, setServicios] = useState([]);
  const [serviciosFiltrados, setServiciosFiltrados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [limiteServicios, setLimiteServicios] = useState(null);
  const [infoPlan, setInfoPlan] = useState(null);
  const [anticipoActivo, setAnticipoActivo] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    duracion_minutos: 30,
    precio: '',
    requiere_deposito: false,
    monto_deposito: ''
  });

  // Verificar si el usuario es administrador
  const isAdmin = user?.rol === 'admin' || user?.rol === 'supervisor';

  // Estilos comunes para inputs
  const inputStyle = {
    border: '2px solid rgba(59, 130, 246, 0.3)',
    borderRadius: '8px',
    padding: '0.75rem',
    fontSize: '1rem',
    backgroundColor: '#fafafa',
    width: '100%',
    transition: 'all 0.2s'
  };

  useEffect(() => {
    if (!authLoading) {
      if (user?.tenant?.acceso_bloqueado === true) {
        setAccesoDenegado(true);
        setMensajeError('Tu suscripción está suspendida. Contacta al administrador.');
        setLoading(false);
        return;
      }
      fetchServicios();
      fetchInfoPlan();
      api.get('/pago-config').then(r => setAnticipoActivo(!!r.data.activo)).catch(() => {});
    }
  }, [authLoading]);

  useEffect(() => {
    // Filtrar servicios cuando cambia el término de búsqueda o la lista
    if (servicios.length > 0) {
      const filtrados = servicios.filter(servicio => 
        servicio.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (servicio.descripcion && servicio.descripcion.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setServiciosFiltrados(filtrados);
    } else {
      setServiciosFiltrados([]);
    }
  }, [searchTerm, servicios]);

  const fetchServicios = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/servicios');
      setServicios(data);
      setServiciosFiltrados(data);
    } catch (error) {
      if (error.response?.status === 403) {
        setAccesoDenegado(true);
        setMensajeError(error.response.data.message || 'No tienes acceso a esta sección.');
      } else {
        toast.error('Error al cargar servicios');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchInfoPlan = async () => {
    try {
      // Obtener información del plan del tenant actual
      const { data } = await api.get('/tenant/plan-info');
      const maxServicios = Number(data.plan?.max_servicios || data.max_servicios || 999999);
      setLimiteServicios(maxServicios);
      setInfoPlan(data);
      
      // Mostrar advertencia si está cerca del límite
      if (data.max_servicios) {
        const porcentaje = (servicios.length / data.max_servicios) * 100;
        if (porcentaje >= 80) {
          toast.warning(`Has alcanzado el ${Math.round(porcentaje)}% de tu límite de servicios (${servicios.length}/${data.max_servicios})`);
        }
      }
    } catch {
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar límite de servicios solo para creación (no para edición)
    if (!editingId && limiteServicios && servicios.length >= limiteServicios) {
      toast.error(`Has alcanzado el límite de ${limiteServicios} servicios de tu plan. Actualiza tu plan para agregar más.`);
      return;
    }

    if (formData.duracion_minutos <= 0) {
      toast.error('La duración debe ser mayor a 0 minutos');
      return;
    }

    if (formData.duracion_minutos > 120) {
      toast.error('La duración no puede exceder los 120 minutos');
      return;
    }

    try {
      const dataToSend = {
        ...formData,
        duracion_minutos: Number(formData.duracion_minutos),
        requiere_deposito: formData.requiere_deposito ? 1 : 0,
        monto_deposito: formData.requiere_deposito ? formData.monto_deposito : null
      };

      if (editingId) {
        await api.put(`/servicios/${editingId}`, dataToSend);
        toast.success('Servicio actualizado');
      } else {
        await api.post('/servicios', dataToSend);
        toast.success('Servicio creado');
      }
      fetchServicios();
      fetchInfoPlan(); // Actualizar info del plan después de crear
      closeModal();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al guardar servicio');
    }
  };

  const handleEdit = (servicio) => {
    setFormData({
      ...servicio,
      requiere_deposito: Boolean(servicio.requiere_deposito)
    });
    setEditingId(servicio.id);
    setShowModal(true);
  };

  const handleDelete = (id, nombre) => {
    if (!isAdmin) {
      toast.error('No tienes permisos para eliminar servicios');
      return;
    }

    setConfirmAction({ onConfirm: async () => {
      try {
        await api.delete(`/servicios/${id}`);
        toast.success('Servicio eliminado correctamente');
        fetchServicios();
        fetchInfoPlan();
      } catch (error) {
        toast.error(error.response?.data?.error || 'Error al eliminar servicio');
      }
    }});
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData({
      nombre: '',
      descripcion: '',
      duracion_minutos: 30,
      precio: '',
      requiere_deposito: false,
      monto_deposito: ''
    });
  };

  if (accesoDenegado) {
    return (
      <Layout>
        <div style={accesoRestringidoStyles.container}>
          <div style={accesoRestringidoStyles.card}>
            <span style={accesoRestringidoStyles.icon}>🔒</span>
            <h2>Acceso Restringido</h2>
            <p style={accesoRestringidoStyles.message}>{mensajeError}</p>
            <p style={accesoRestringidoStyles.suggestion}>
              Si crees que esto es un error, contacta al administrador del sistema para revisar los detalles de tu plan.
            </p>
            <button
              style={accesoRestringidoStyles.button}
              onClick={() => navigate('/')}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
            >
              Volver al Dashboard
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="text-center" style={{ padding: '3rem' }}>
          <div className="loading"></div>
          <p style={{ marginTop: '1rem', color: 'var(--text-secondary)' }}>
            Cargando servicios...
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-header">
        <div>
          <h2>💼 Servicios</h2>
          {limiteServicios && (
            <div style={{ 
              marginTop: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '1rem'
            }}>
              <div style={{ 
                fontSize: '0.875rem',
                color: 'var(--text-secondary)',
                background: 'var(--background)',
                padding: '0.25rem 0.75rem',
                borderRadius: '20px'
              }}>
                📊 Servicios: {servicios.length} / {limiteServicios}
              </div>
              {limiteServicios && servicios.length >= limiteServicios && (
                <span style={{ 
                  fontSize: '0.875rem',
                  color: '#dc2626',
                  background: '#fee',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '20px'
                }}>
                  ⚠️ Límite alcanzado
                </span>
              )}
            </div>
          )}
        </div>
        <button 
          className="btn btn-primary" 
          onClick={() => setShowModal(true)}
          disabled={limiteServicios && servicios.length >= limiteServicios}
          title={limiteServicios && servicios.length >= limiteServicios ? 'Has alcanzado el límite de servicios de tu plan' : ''}
        >
          + Nuevo Servicio
        </button>
      </div>

      {/* Buscador */}
      <div className="card mb-3">
        <input
          type="text"
          className="form-input"
          placeholder="🔍 Buscar servicio por nombre o descripción..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={inputStyle}
          onFocus={(e) => {
            e.target.style.borderColor = 'rgba(59, 130, 246, 0.6)';
            e.target.style.backgroundColor = '#ffffff';
            e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = 'rgba(59, 130, 246, 0.3)';
            e.target.style.backgroundColor = '#fafafa';
            e.target.style.boxShadow = 'none';
          }}
        />
        {searchTerm && (
          <div style={{ 
            marginTop: '0.5rem', 
            fontSize: '0.875rem', 
            color: 'var(--text-secondary)',
            textAlign: 'right'
          }}>
            {serviciosFiltrados.length} resultado(s) encontrado(s)
          </div>
        )}
      </div>

      {serviciosFiltrados.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
          <span style={{ fontSize: '3rem' }}>💼</span>
          <h3 style={{ marginTop: '1rem' }}>
            {searchTerm ? 'No hay resultados para tu búsqueda' : 'No hay servicios creados'}
          </h3>
          <p style={{ color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
            {searchTerm 
              ? 'Prueba con otros términos de búsqueda' 
              : 'Crea tu primer servicio haciendo click en el botón "+ Nuevo Servicio"'}
          </p>
        </div>
      ) : (
        <div className="grid grid-3">
          {serviciosFiltrados.map(servicio => (
            <div key={servicio.id} className="card">
              <div className="flex justify-between items-center mb-2">
                <h3 style={{ fontSize: '1.25rem', fontWeight: 600 }}>{servicio.nombre}</h3>
                <span className="badge badge-success">${servicio.precio}</span>
              </div>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                {servicio.descripcion || 'Sin descripción'}
              </p>
              <div style={{ marginBottom: '1rem' }}>
                <span className="badge badge-info">{servicio.duracion_minutos} min</span>
                {!!servicio.requiere_deposito && (
                  <span className="badge badge-warning ml-2">
                    Depósito: ${servicio.monto_deposito}
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                {(user?.rol === 'admin' || user?.puede_modificar_servicios) && (
                  <button className="btn btn-sm btn-secondary" onClick={() => handleEdit(servicio)}>
                    ✏️ Editar
                  </button>
                )}
                {(user?.rol === 'admin' || user?.puede_eliminar_servicios) && (
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleDelete(servicio.id, servicio.nombre)}
                  >
                    🗑️ Eliminar
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              padding: '1.5rem 1.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '10px',
                  width: '42px', height: '42px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.3rem',
                }}>
                  💼
                </div>
                <div>
                  <h3 style={{ margin: 0, color: 'white', fontSize: '1.15rem', fontWeight: '700' }}>
                    {editingId ? 'Editar Servicio' : 'Nuevo Servicio'}
                  </h3>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
                    {editingId ? 'Modifica la información del servicio' : 'Completa los datos para agregar un nuevo servicio'}
                  </p>
                </div>
              </div>
              <button
                onClick={closeModal}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  width: '32px', height: '32px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body" style={{ padding: '1.5rem 1.75rem', maxHeight: '70vh', overflowY: 'auto' }}>

                {/* Información del servicio */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
                    <span style={{ background: '#eff6ff', color: '#2563eb', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: '700' }}>💼 Información del servicio</span>
                  </div>
                  <div className="form-group" style={{ marginBottom: '1rem' }}>
                    <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.875rem' }}>
                      Nombre del servicio <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <input type="text" className="form-input" value={formData.nombre}
                      onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                      required placeholder="Ej. Corte de cabello" style={inputStyle}
                      onFocus={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.6)'; e.target.style.backgroundColor = '#fff'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)'; }}
                      onBlur={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.3)'; e.target.style.backgroundColor = '#fafafa'; e.target.style.boxShadow = 'none'; }}
                    />
                  </div>
                  <div className="form-group">
                    <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Descripción</label>
                    <textarea className="form-input" value={formData.descripcion}
                      onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
                      rows={3} placeholder="Describe brevemente el servicio..." style={{ ...inputStyle, resize: 'vertical' }}
                      onFocus={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.6)'; e.target.style.backgroundColor = '#fff'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)'; }}
                      onBlur={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.3)'; e.target.style.backgroundColor = '#fafafa'; e.target.style.boxShadow = 'none'; }}
                    />
                  </div>
                </div>

                {/* Configuración */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
                    <span style={{ background: '#f0fdf4', color: '#16a34a', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: '700' }}>⚙️ Configuración</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                    <div className="form-group">
                      <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.875rem' }}>
                        Duración (min) <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <input type="number" min="1" max="120" step="1" className="form-input"
                        value={formData.duracion_minutos}
                        onChange={(e) => setFormData({...formData, duracion_minutos: e.target.value})}
                        required placeholder="Ej. 60" style={inputStyle}
                        onFocus={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.6)'; e.target.style.backgroundColor = '#fff'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)'; }}
                        onBlur={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.3)'; e.target.style.backgroundColor = '#fafafa'; e.target.style.boxShadow = 'none'; }}
                      />
                      <small style={{ color: 'var(--text-secondary)', marginTop: '0.25rem', display: 'block' }}>Máximo 120 min</small>
                    </div>
                    <div className="form-group">
                      <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.875rem' }}>
                        Precio <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <input type="number" step="0.01" min="0" className="form-input"
                        value={formData.precio}
                        onChange={(e) => setFormData({...formData, precio: e.target.value})}
                        required placeholder="0.00" style={inputStyle}
                        onFocus={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.6)'; e.target.style.backgroundColor = '#fff'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)'; }}
                        onBlur={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.3)'; e.target.style.backgroundColor = '#fafafa'; e.target.style.boxShadow = 'none'; }}
                      />
                    </div>
                  </div>
                  <div className="form-group" style={{ marginBottom: formData.requiere_deposito ? '1rem' : 0 }}>
                    <label style={{ cursor: anticipoActivo ? 'pointer' : 'not-allowed', opacity: anticipoActivo ? 1 : 0.5, display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                      title={!anticipoActivo ? 'Activa "Requerir anticipo al reservar" en Configuración para usar esta opción' : ''}>
                      <input type="checkbox" checked={formData.requiere_deposito} disabled={!anticipoActivo}
                        onChange={(e) => setFormData({...formData, requiere_deposito: e.target.checked, monto_deposito: e.target.checked ? formData.monto_deposito : ''})}
                        style={{ width: '18px', height: '18px', cursor: anticipoActivo ? 'pointer' : 'not-allowed' }}
                      />
                      <span style={{ fontWeight: '500', color: '#374151', fontSize: '0.875rem' }}>Requiere depósito</span>
                      {!anticipoActivo && <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>(requiere anticipo activo)</span>}
                    </label>
                  </div>
                  {formData.requiere_deposito && (
                    <div className="form-group">
                      <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.875rem' }}>
                        Monto del depósito <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <input type="number" step="0.01" min="0" className="form-input"
                        value={formData.monto_deposito}
                        onChange={(e) => setFormData({...formData, monto_deposito: e.target.value})}
                        required placeholder="0.00" style={inputStyle}
                        onFocus={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.6)'; e.target.style.backgroundColor = '#fff'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)'; }}
                        onBlur={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.3)'; e.target.style.backgroundColor = '#fafafa'; e.target.style.boxShadow = 'none'; }}
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer" style={{ borderTop: '1px solid #e5e7eb', padding: '1rem 1.75rem' }}>
                <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancelar</button>
                <button type="submit" className="btn btn-primary" style={{ minWidth: '120px' }}>
                  {editingId ? 'Guardar cambios' : '+ Crear servicio'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {confirmAction && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              padding: '1.25rem 1.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                <h3 style={{ margin: 0, color: 'white', fontSize: '1rem', fontWeight: '700' }}>
                  Confirmar acción
                </h3>
              </div>
              <button
                onClick={() => setConfirmAction(null)}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  width: '28px', height: '28px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >✕</button>
            </div>
            <div className="modal-body">
              <p style={{ color: '#374151' }}>¿Confirmas esta acción?</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setConfirmAction(null)}>No</button>
              <button className="btn btn-primary" onClick={() => { confirmAction.onConfirm(); setConfirmAction(null); }}>Sí</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Servicios;
